import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { formatTime } from "./utils.js";
import "./TimelinePreview.css";

/**
 * Стоп-кадр над курсором на таймлайне. Чисто презентационный компонент:
 * позицию и время ему даёт родитель (в 7cut это VideoTimeline — там уже есть
 * hoverPosition / hoverTime / isTimelineHovered).
 *
 * Класть внутрь элемента с position: relative (в 7cut — .timeline-container).
 *
 * props:
 *   visible      boolean
 *   time         секунды под курсором
 *   leftPercent  положение по горизонтали в % ширины контейнера
 *   frameSource  объект из frameSources.js (может быть null — тогда только время)
 *   width        ширина кадра в px (по умолчанию 160)
 *   bottom       отступ снизу в px до края контейнера (по умолчанию 58 — выше плашки времени 7cut)
 *   label        подпись вместо времени (необязательно)
 */
export default function TimelinePreview({
  visible,
  time,
  leftPercent,
  frameSource,
  width = 160,
  bottom = 58,
  label,
  className = "",
}) {
  const [ready, setReady] = useState(false);
  const [, bump] = useState(0);
  const boxRef = useRef(null);

  // Ждём готовности источника (VTT скачан / метаданные видео получены).
  useEffect(() => {
    setReady(false);
    if (!frameSource) return undefined;
    let alive = true;
    frameSource.ready.then(() => alive && setReady(true)).catch(() => {});
    const unsub = frameSource.subscribe(() => alive && bump((n) => n + 1));
    return () => { alive = false; unsub(); };
  }, [frameSource]);

  const frame = visible && ready && frameSource ? frameSource.getFrame(time) : null;
  const aspect = frameSource?.aspect || (frame?.type === "sprite" ? frame.w / frame.h : 16 / 9);
  const height = Math.round(width / aspect);

  // canvas живёт в источнике; просто вставляем его в разметку.
  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el || frame?.type !== "canvas") return undefined;
    const canvas = frame.canvas;
    canvas.className = "tlp__canvas";
    el.appendChild(canvas);
    return () => { if (canvas.parentNode === el) el.removeChild(canvas); };
  }, [frame?.type, frame?.canvas]);

  if (!visible) return null;

  const half = Math.round(width / 2) + 4;
  const style = {
    "--tlp-w": `${width}px`,
    "--tlp-h": `${height}px`,
    bottom: `${bottom}px`,
    // Не даём кадру вылезать за края контейнера.
    left: `clamp(${half}px, ${leftPercent}%, calc(100% - ${half}px))`,
  };

  return (
    <div className={`tlp ${className}`} style={style} aria-hidden="true">
      <div className="tlp__frame" ref={boxRef} data-kind={frame?.type || "empty"}>
        {frame?.type === "sprite" && (
          <div
            className="tlp__sprite"
            style={{
              width: frame.w,
              height: frame.h,
              backgroundImage: `url("${frame.url}")`,
              backgroundPosition: `-${frame.x}px -${frame.y}px`,
              transform: `scale(${width / frame.w})`,
            }}
          />
        )}
        {frame?.type === "image" && <img className="tlp__img" src={frame.url} alt="" draggable={false} />}
        {!frame && <div className="tlp__empty" />}
      </div>
      <div className="tlp__time">{label ?? formatTime(time)}</div>
    </div>
  );
}
